import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { Account } from '../../entities/Account';
import { AccountUser } from '../../entities/AccountUser';
import { User } from '../../entities/User';
import { supabase } from '../../lib/supabase';

interface AccountContextType {
  currentAccountId: string | null;
  accounts: Account[];
  isLoading: boolean;
  isReady: boolean; // Adicionado para indicar que o contexto está pronto
  error: string | null;
  switchAccount: (accountId: string) => Promise<void>;
  refreshAccounts: () => Promise<void>;
  clearError: () => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, ready: authReady } = useAuth(); // Usar o `ready` do AuthProvider
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false); // Novo estado de "pronto"
  const loadAccountsPromiseRef = useRef<Promise<void> | null>(null);
  const autoCreateAccountPromiseRef = useRef<Promise<Account> | null>(null);

  // Função para limpar erro
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Função para atualizar user_metadata no Supabase Auth
  const updateUserMetadata = useCallback(async (metadata: Record<string, any>) => {
    if (!authUser) throw new Error('Usuário não autenticado');
    const { error } = await supabase.auth.updateUser({ data: metadata });
    if (error) throw error;
    await supabase.auth.refreshSession();
  }, [authUser]);

  // Função para carregar contas do usuário
  const loadAccounts = useCallback(async () => {
    if (!authUser) {
      setIsLoading(false);
      return;
    }

    if (loadAccountsPromiseRef.current) {
      await loadAccountsPromiseRef.current;
      return;
    }

    let didTimeout = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const run = (async () => {
      setIsLoading(true);
      setError(null);
      
      // Timeout de segurança para evitar loading infinito
      timeoutId = setTimeout(() => {
        didTimeout = true;
        console.warn('⚠️ [AccountContext] Loading timeout - forçando finalização do loading');
        setIsLoading(false);
        setError('Timeout ao carregar contas. Tente recarregar a página.');
      }, 10000); // 10 segundos

      const userId = authUser.id;

      let userData: Awaited<ReturnType<typeof User.me>> = null;
      try {
        userData = await User.me();
      } catch (error) {
        userData = null;
      }
      if (didTimeout) return;

      // Buscar contas do usuário usando AccountUser
      const userAccountLinks = await AccountUser.filter({ user_id: userId });
      if (didTimeout) return;
      
      // Se usuário não tem contas, criar primeira conta automaticamente
      if (userAccountLinks.length === 0) {
        console.log('🏦 Usuário sem contas - criando primeira conta automaticamente...');
        
        try {
          const defaultAccountName = `Conta de ${
            userData?.full_name ||
            authUser.user_metadata?.full_name ||
            authUser.user_metadata?.name ||
            authUser.email?.split('@')[0] ||
            'Usuário'
          }`;

          if (!autoCreateAccountPromiseRef.current) {
            autoCreateAccountPromiseRef.current = Account.create({ name: defaultAccountName });
          }

          const createdAccount = await autoCreateAccountPromiseRef.current;
          if (!createdAccount?.id) throw new Error('Falha ao criar primeira conta');

          setCurrentAccountId(createdAccount.id);
          setAccounts([createdAccount]);
        } catch (autoCreateError) {
          console.error('Erro na criação automática da primeira conta:', autoCreateError);
          setError('Erro ao criar primeira conta. Tente recarregar a página.');
          setAccounts([]);
        } finally {
          autoCreateAccountPromiseRef.current = null;
        }
      } else {
        // Usuário já tem contas - carregar normalmente
        const accountIds = userAccountLinks.map((ua: any) => ua.account_id);
        const { data: userAccounts, error: accountsError } = await supabase
          .from('accounts')
          .select('*')
          .in('id', accountIds);

        if (accountsError) throw accountsError;
        setAccounts(userAccounts || []);

        const metadataAccountId = authUser.user_metadata?.current_account_id as string | undefined;
        const validCurrentAccountId = metadataAccountId && accountIds.includes(metadataAccountId)
          ? metadataAccountId
          : String(accountIds[0]);

        if (validCurrentAccountId) {
          setCurrentAccountId(validCurrentAccountId);
          if (metadataAccountId !== validCurrentAccountId) {
            await updateUserMetadata({ current_account_id: validCurrentAccountId });
          }
        }
      }
      
    })();

    loadAccountsPromiseRef.current = run;

    try {
      await run;
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      setError('Erro ao carregar contas. Tente novamente.');
      setAccounts([]);
    } finally {
      loadAccountsPromiseRef.current = null;
      if (timeoutId) clearTimeout(timeoutId);
      setIsLoading(false);
      setIsReady(true); // Marca como pronto no final, mesmo com erro
    }
  }, [authUser, updateUserMetadata]);

  // Inicializar estado quando usuário muda
  useEffect(() => {
    if (authReady) {
      if (authUser) {
        setIsReady(false);
        setIsLoading(true);
        const accountId = authUser.user_metadata?.current_account_id;
        setCurrentAccountId(accountId || null);
        void loadAccounts();
      } else {
        // Reset state quando não há usuário
        setCurrentAccountId(null);
        setAccounts([]);
        setIsLoading(false);
        setError(null);
        setIsReady(true); // Fica pronto quando o usuário é deslogado
      }
    }
  }, [authReady, authUser, loadAccounts]);

  // Listener para eventos de criação de conta
  useEffect(() => {
    const handleAccountCreated = (event: CustomEvent) => {
      console.log('🔄 Nova conta criada, recarregando lista de contas...');
      loadAccounts();
    };

    window.addEventListener('accountCreated', handleAccountCreated as EventListener);
    
    return () => {
      window.removeEventListener('accountCreated', handleAccountCreated as EventListener);
    };
  }, [loadAccounts]);

  // Função para trocar conta
  const switchAccount = useCallback(async (accountId: string) => {
    if (!authUser) {
      throw new Error('Usuário não autenticado');
    }

    if (currentAccountId === accountId) {
      // Já está na conta selecionada
      return;
    }

    const previousAccountId = currentAccountId;

    try {
      setIsLoading(true);
      setError(null);
      
      // Primeiro, recarregar a lista de contas para garantir que está atualizada
      await loadAccounts();
      
      // Verificar se a conta existe na lista de contas do usuário
      // Usar uma verificação mais robusta que também consulta o banco se necessário
      let accountExists = accounts.find(account => account.id === accountId);
      
      if (!accountExists) {
        // Se não encontrou na lista local, tentar buscar diretamente no banco
        try {
          const userAccountLinks = await AccountUser.filter({ user_id: authUser.id });
          const hasAccess = userAccountLinks.some((ua: any) => ua.account_id === accountId);
          
          if (!hasAccess) {
            throw new Error('Conta não encontrada ou sem permissão de acesso');
          }
          
          // Se tem acesso, buscar os dados da conta
          const { data: accountData, error: accountError } = await supabase
            .from('accounts')
            .select('*')
            .eq('id', accountId)
            .single();
            
          if (accountError || !accountData) {
            throw new Error('Conta não encontrada ou sem permissão de acesso');
          }
          
          accountExists = accountData;
        } catch (dbError) {
          throw new Error('Conta não encontrada ou sem permissão de acesso');
        }
      }
      
      // Atualizar no Supabase Auth e garantir atualização do token
      await updateUserMetadata({ current_account_id: accountId });
      
      // Atualizar estado local imediatamente
      setCurrentAccountId(accountId);
      
      // Recarregar contas para garantir sincronização
      await loadAccounts();
      
      // Disparar evento customizado para notificar componentes sobre a mudança
      const event = new CustomEvent('accountChanged', { 
        detail: { 
          newAccountId: accountId, 
          previousAccountId 
        } 
      });
      window.dispatchEvent(event);
      
      console.log('✅ Conta alterada com sucesso:', { from: previousAccountId, to: accountId });
      
    } catch (error) {
      console.error('❌ Erro ao trocar conta:', error);
      
      // Rollback em caso de erro
      if (previousAccountId) {
        setCurrentAccountId(previousAccountId);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Erro ao trocar conta';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [authUser, currentAccountId, accounts, updateUserMetadata, loadAccounts]);

  // Função para atualizar contas
  const refreshAccounts = useCallback(async () => {
    await loadAccounts();
  }, [loadAccounts]);

  const value: AccountContextType = {
    currentAccountId,
    accounts,
    isLoading,
    isReady, // Expor o estado de "pronto"
    error,
    switchAccount,
    refreshAccounts,
    clearError
  };

  return (
    <AccountContext.Provider value={value}>
      {isReady ? children : null} 
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}

export function useCurrentAccount() {
  const { accounts, currentAccountId } = useAccount();
  return accounts.find(account => account.id === currentAccountId) || null;
}

export function useIsAccountActive(accountId: string) {
  const { currentAccountId } = useAccount();
  return currentAccountId === accountId;
}

export function useAccountChangeEffect(callback: (accountId: string | null) => void | Promise<void>, deps: any[] = []) {
  const { currentAccountId } = useAccount();
  
  useEffect(() => {
    const handleAccountChange = async () => {
      try {
        await callback(currentAccountId);
      } catch (error) {
        console.error('Error in account change effect:', error);
      }
    };
    
    handleAccountChange();
  }, [currentAccountId, ...deps]);
}

export function useAccountDataReloader(
  reloadFunction: (accountId: string) => Promise<void> | void,
  deps: any[] = []
) {
  const { currentAccountId } = useAccount();
  
  useEffect(() => {
    const handleAccountChanged = async (event: CustomEvent) => {
      const { newAccountId } = event.detail;
      if (newAccountId && typeof reloadFunction === 'function') {
        try {
          await reloadFunction(newAccountId);
        } catch (error) {
          console.error('Error reloading data after account change:', error);
        }
      }
    };

    window.addEventListener('accountChanged', handleAccountChanged as EventListener);
    
    return () => {
      window.removeEventListener('accountChanged', handleAccountChanged as EventListener);
    };
  }, [reloadFunction, ...deps]);
}
